/**
 * Буфер карточки расходника (DictConsCard): wire → UI-строки и обратно в тело PUT /bindings.
 * Вынесено из DictConsCard 05.10.2026, когда добавился пятый скоуп «Филиалы» — общий расход
 * филиала на час работы или на рабочий день (электричество, вода, туалетная бумага).
 */
import type { ConsBindingsBody, ConsCardWire } from "../../api/dictTypes";
import type { ConsUiRow, Per } from "./DictBlocks";
import { UNITS } from "./DictUnitCtl";

export interface ConsSt {
  n: string; u: string; custom: boolean; cost: number;
  qs: ConsUiRow[]; ts: ConsUiRow[]; os: ConsUiRow[]; ss: ConsUiRow[]; bs: ConsUiRow[];
  qO: boolean; tO: boolean; oO: boolean; sO: boolean; bO: boolean;
  /** Сервер прислал филиальные нормы. Нет — branches в PUT не шлём: пустой блок не стирает нормы. */
  brKnown: boolean;
}

/** Буфер из wire: per-кодировка по-таблично (Д11) → UI-коды эталона. */
export const initSt = (w: ConsCardWire): ConsSt => {
  const u = w.consumable.unit || "шт";
  const qs = w.consumers.quests.map((x) => ({
    id: x.quest_id, name: x.name, qty: x.qty, per: (x.per_player ? "pl" : "ga") as Per, round: x.round_to || 0,
  }));
  const ts = w.consumers.teas.map((x) => ({
    id: x.tea_room_id, name: x.name, qty: x.qty, per: (x.per_guest ? "gu" : "tea") as Per, round: x.round_to || 0,
  }));
  const os = w.consumers.operators.map((x) => ({
    id: x.target, name: x.name, qty: x.qty,
    per: (x.days > 0 ? "dd" : "sh") as Per, days: x.days > 0 ? x.days : undefined, round: x.round_to || 0,
  }));
  const ss = w.consumers.services.map((x) => ({
    id: x.service_id, name: x.name, qty: x.qty, per: (x.per_player ? "pl" : "ga") as Per, round: x.round_to || 0,
  }));
  /* branches нет у старого сервера — пустой блок, а не падение карточки */
  const bs = (w.consumers.branches ?? []).map((x) => ({
    id: x.branch_id, name: x.name, qty: x.qty, per: (x.per_hour ? "hr" : "dy") as Per, round: x.round_to || 0,
  }));
  return {
    n: w.consumable.name, u, custom: !UNITS.includes(u), cost: w.consumable.cost || 0,
    qs, ts, os, ss, bs, brKnown: Array.isArray(w.consumers.branches),
    qO: qs.length + w.consumers.versions.length > 0,
    tO: ts.length > 0, oO: os.length > 0, sO: ss.length > 0, bO: bs.length > 0,
  };
};

/** Тело PUT /api/consumables/:id/bindings из буфера (replace-set всех скоупов). */
export function bindingsBody(st: ConsSt): ConsBindingsBody {
  return {
    quests: st.qs.map((r) => ({ quest_id: r.id, qty: r.qty, per_player: r.per === "pl" ? 1 : 0, round_to: r.round ?? 0 })),
    teas: st.ts.map((r) => ({ tea_room_id: r.id, qty: r.qty, per_guest: r.per === "gu" ? 1 : 0, round_to: r.round ?? 0 })),
    operators: st.os.map((r) => ({
      target: r.id as "lead" | "hall_admin", qty: r.qty, days: r.per === "dd" ? r.days || 30 : 0, round_to: r.round ?? 0,
    })),
    services: st.ss.map((r) => ({ service_id: r.id, qty: r.qty, per_player: r.per === "pl" ? 1 : 0, round_to: r.round ?? 0 })),
    ...(st.brKnown ? {
      branches: st.bs.map((r) => ({ branch_id: r.id, qty: r.qty, per_hour: r.per === "hr" ? 1 : 0, round_to: r.round ?? 0 })),
    } : {}),
  };
}

/** Потребители не менялись — PUT /bindings не нужен. 05.10: ОО правит название и цену без ложного
 *  «не сохранилось» (нормы квестов и услуг сервер пускает только ЕО), и легаси-округление
 *  справочника не переписывается в привязки, пока их никто не трогал. */
export const sameBindings = (a: ConsSt, b: ConsSt): boolean =>
  JSON.stringify(bindingsBody(a)) === JSON.stringify(bindingsBody(b));
